import { useState, useEffect } from "react";
import {
  Truck, Plus, Edit2, Trash2, X, RefreshCw, Check,
  Search, Phone, Mail, MapPin, Package, Link, Star,
  ToggleLeft, ToggleRight,
} from "lucide-react";
import { useAdmin } from "../admin-context";

// ── Constants ─────────────────────────────────────────────────────────────────
const STATUS_STYLES = {
  active:   "bg-emerald-100 text-emerald-700",
  inactive: "bg-gray-100 text-gray-500",
  pending:  "bg-yellow-100 text-yellow-700",
};

const SUPPLY_TYPES = ["Produce", "Dairy", "Meat & Poultry", "Beverages", "Dry Goods", "Packaging", "Spices", "Bakery", "Seafood", "Other"];

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—";

// ── Reusable UI ───────────────────────────────────────────────────────────────
const Drawer = ({ open, onClose, title, children }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white h-full max-w-md w-full shadow-2xl flex flex-col overflow-hidden animate-in slide-in-from-right duration-200">
        <div className="flex items-center justify-between px-5 py-4 border-b flex-shrink-0">
          <h2 className="text-lg font-bold text-gray-900">{title}</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
            <X size={18} className="text-gray-500" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-5">{children}</div>
      </div>
    </div>
  );
};

const FF = ({ label, required, children }) => (
  <div>
    <label className="block text-sm font-semibold text-gray-700 mb-1.5">
      {label}{required && <span className="text-red-500 ml-0.5">*</span>}
    </label>
    {children}
  </div>
);

const inp = "w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent";
const sel = inp + " bg-white";

// ── Main Page ─────────────────────────────────────────────────────────────────
const VendorsPage = () => {
  const { vendors, loadVendors, createVendor, updateVendor, deleteVendor, showToast } = useAdmin();

  const [search,     setSearch]     = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing,    setEditing]    = useState(null);
  const [deleteId,   setDeleteId]   = useState(null);
  const [viewItem,   setViewItem]   = useState(null);
  const [saving,     setSaving]     = useState(false);

  const emptyForm = {
    name: "", contact_person: "", email: "", phone: "",
    address: "", supply_type: SUPPLY_TYPES[0], status: "active",
    website: "", payment_terms: "", notes: "", rating: 5,
  };
  const [form, setForm] = useState(emptyForm);

  useEffect(() => { loadVendors && loadVendors(); }, []);

  const f = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }));

  const openAdd = () => { setEditing(null); setForm(emptyForm); setDrawerOpen(true); };
  const openEdit = (v) => {
    setEditing(v);
    setForm({
      name: v.name, contact_person: v.contact_person || "", email: v.email || "",
      phone: v.phone || "", address: v.address || "", supply_type: v.supply_type || SUPPLY_TYPES[0],
      status: v.status || "active", website: v.website || "",
      payment_terms: v.payment_terms || "", notes: v.notes || "", rating: v.rating ?? 5,
    });
    setDrawerOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) return showToast("Vendor name is required", "error");
    setSaving(true);
    try {
      if (editing) {
        await updateVendor(editing.id, form);
        showToast("Vendor updated");
      } else {
        await createVendor(form);
        showToast("Vendor added");
      }
      setDrawerOpen(false);
    } catch {
      showToast("Failed to save", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteVendor(id);
      setDeleteId(null);
      setViewItem(null);
      showToast("Vendor deleted");
    } catch {
      showToast("Failed to delete", "error");
    }
  };

  const allTypes = [...new Set((vendors || []).map(v => v.supply_type).filter(Boolean))];

  const list = (vendors || []).filter(v => {
    const matchType = typeFilter === "all" || v.supply_type === typeFilter;
    const q = search.toLowerCase();
    const matchSearch = !q
      || (v.name || "").toLowerCase().includes(q)
      || (v.contact_person || "").toLowerCase().includes(q)
      || (v.email || "").toLowerCase().includes(q)
      || (v.supply_type || "").toLowerCase().includes(q);
    return matchType && matchSearch;
  });

  const stats = [
    { label: "Total",    count: (vendors || []).length,                                  color: "bg-gray-50 text-gray-700"    },
    { label: "Active",   count: (vendors || []).filter(v => v.status === "active").length,  color: "bg-emerald-50 text-emerald-700" },
    { label: "Inactive", count: (vendors || []).filter(v => v.status === "inactive").length, color: "bg-red-50 text-red-700"      },
    { label: "Pending",  count: (vendors || []).filter(v => v.status === "pending").length,  color: "bg-amber-50 text-amber-700"   },
  ];

  const RatingStars = ({ value }) => (
    <div className="flex gap-0.5">
      {[1,2,3,4,5].map(i => (
        <Star key={i} size={11} className={i <= value ? "fill-amber-400 text-amber-400" : "text-gray-200"} />
      ))}
    </div>
  );

  return (
    <div className="p-6 space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-extrabold text-gray-900">Vendors & Suppliers</h1>
          <p className="text-sm text-gray-500 mt-0.5">Manage your supply chain and vendor relationships</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => loadVendors && loadVendors()} className="p-2 rounded-xl border border-gray-200 hover:bg-gray-50 transition">
            <RefreshCw size={16} className="text-gray-500" />
          </button>
          <button onClick={openAdd}
            className="flex items-center gap-2 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-xl font-semibold text-sm transition">
            <Plus size={16} /> Add Vendor
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {stats.map(({ label, count, color }) => (
          <div key={label} className={`rounded-2xl p-4 ${color}`}>
            <p className="text-2xl font-extrabold">{count}</p>
            <p className="text-xs font-semibold mt-0.5">{label}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            className="w-full pl-9 pr-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
            placeholder="Search vendors…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <div className="flex flex-wrap gap-2">
          {["all", ...allTypes].map(t => (
            <button key={t}
              onClick={() => setTypeFilter(t)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition capitalize
                ${typeFilter === t ? "bg-orange-500 text-white border-orange-500" : "bg-white text-gray-600 border-gray-200 hover:border-orange-300"}`}>
              {t}
            </button>
          ))}
        </div>
      </div>

      {/* Cards grid */}
      {list.length === 0 ? (
        <div className="py-16 text-center text-gray-400">
          <Truck size={32} className="mx-auto mb-3 opacity-30" />
          <p className="font-semibold">No vendors found</p>
          <p className="text-sm">Add your first vendor to get started</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {list.map(v => (
            <div key={v.id}
              className="bg-white rounded-2xl border border-gray-200 p-5 hover:shadow-md transition group cursor-pointer"
              onClick={() => setViewItem(v)}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center flex-shrink-0">
                    <Truck size={18} className="text-orange-500" />
                  </div>
                  <div>
                    <p className="font-bold text-gray-900 text-sm">{v.name}</p>
                    <p className="text-xs text-gray-400">{v.contact_person}</p>
                  </div>
                </div>
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${STATUS_STYLES[v.status] || "bg-gray-100 text-gray-500"}`}>
                  {v.status}
                </span>
              </div>

              <div className="space-y-1 mb-3">
                {v.email && <p className="text-xs text-gray-500 flex items-center gap-1.5"><Mail size={11} className="text-gray-400" />{v.email}</p>}
                {v.phone && <p className="text-xs text-gray-500 flex items-center gap-1.5"><Phone size={11} className="text-gray-400" />{v.phone}</p>}
                {v.address && <p className="text-xs text-gray-500 flex items-center gap-1.5"><MapPin size={11} className="text-gray-400" />{v.address}</p>}
              </div>

              <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                <span className="text-xs font-semibold text-orange-600 bg-orange-50 px-2 py-0.5 rounded-full">
                  {v.supply_type || "General"}
                </span>
                <RatingStars value={v.rating || 5} />
              </div>

              <div className="flex gap-1 mt-3 opacity-0 group-hover:opacity-100 transition">
                <button
                  onClick={(e) => { e.stopPropagation(); openEdit(v); }}
                  className="flex-1 py-1.5 text-xs font-semibold bg-orange-50 hover:bg-orange-100 text-orange-600 rounded-lg transition"
                >
                  Edit
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); setDeleteId(v.id); }}
                  className="flex-1 py-1.5 text-xs font-semibold bg-red-50 hover:bg-red-100 text-red-500 rounded-lg transition"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Add/Edit Drawer ── */}
      <Drawer open={drawerOpen} onClose={() => setDrawerOpen(false)} title={editing ? "Edit Vendor" : "Add Vendor"}>
        <div className="space-y-4">
          <FF label="Vendor / Company Name" required>
            <input className={inp} value={form.name} onChange={f("name")} placeholder="e.g. Fresh Farm Supplies" />
          </FF>
          <FF label="Contact Person">
            <input className={inp} value={form.contact_person} onChange={f("contact_person")} placeholder="Primary contact name" />
          </FF>
          <div className="grid grid-cols-2 gap-3">
            <FF label="Email">
              <input type="email" className={inp} value={form.email} onChange={f("email")} />
            </FF>
            <FF label="Phone">
              <input className={inp} value={form.phone} onChange={f("phone")} />
            </FF>
          </div>
          <FF label="Address">
            <textarea rows={2} className={inp} value={form.address} onChange={f("address")} />
          </FF>
          <div className="grid grid-cols-2 gap-3">
            <FF label="Supply Type">
              <select className={sel} value={form.supply_type} onChange={f("supply_type")}>
                {SUPPLY_TYPES.map(t => <option key={t}>{t}</option>)}
              </select>
            </FF>
            <FF label="Status">
              <select className={sel} value={form.status} onChange={f("status")}>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="pending">Pending</option>
              </select>
            </FF>
          </div>
          <FF label="Website">
            <input className={inp} value={form.website} onChange={f("website")} placeholder="https://…" />
          </FF>
          <FF label="Payment Terms">
            <input className={inp} value={form.payment_terms} onChange={f("payment_terms")} placeholder="e.g. Net 30, COD" />
          </FF>
          <FF label="Rating (1–5)">
            <div className="flex gap-2 items-center">
              <input type="range" min={1} max={5} className="flex-1" value={form.rating}
                onChange={e => setForm(p => ({...p, rating: Number(e.target.value)}))} />
              <span className="text-sm font-bold text-amber-500 w-6 text-center">{form.rating}</span>
            </div>
          </FF>
          <FF label="Notes">
            <textarea rows={3} className={inp} value={form.notes} onChange={f("notes")} placeholder="Internal notes…" />
          </FF>
          <div className="flex gap-3 pt-2">
            <button onClick={() => setDrawerOpen(false)} className="flex-1 py-2.5 border border-gray-200 rounded-xl text-sm font-semibold text-gray-600 hover:bg-gray-50">Cancel</button>
            <button onClick={handleSave} disabled={saving}
              className="flex-1 py-2.5 bg-orange-500 hover:bg-orange-600 text-white rounded-xl text-sm font-semibold flex items-center justify-center gap-2">
              {saving ? <RefreshCw size={14} className="animate-spin" /> : <Check size={14} />}
              {editing ? "Save Changes" : "Add Vendor"}
            </button>
          </div>
        </div>
      </Drawer>

      {/* ── View Drawer ── */}
      {viewItem && (
        <Drawer open={!!viewItem} onClose={() => setViewItem(null)} title="Vendor Details">
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-orange-100 flex items-center justify-center flex-shrink-0">
                <Truck size={24} className="text-orange-500" />
              </div>
              <div>
                <h3 className="text-lg font-extrabold text-gray-900">{viewItem.name}</h3>
                <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full ${STATUS_STYLES[viewItem.status] || "bg-gray-100 text-gray-500"}`}>
                  {viewItem.status}
                </span>
              </div>
            </div>

            <div className="bg-gray-50 rounded-xl p-4 space-y-2 text-sm">
              {[
                ["Contact", viewItem.contact_person],
                ["Email",   viewItem.email],
                ["Phone",   viewItem.phone],
                ["Address", viewItem.address],
                ["Type",    viewItem.supply_type],
                ["Terms",   viewItem.payment_terms],
                ["Website", viewItem.website],
                ["Since",   fmtDate(viewItem.created_at)],
              ].filter(([,v]) => v).map(([k, v]) => (
                <div key={k} className="flex justify-between gap-4">
                  <span className="text-gray-500 flex-shrink-0">{k}</span>
                  <span className="font-semibold text-gray-900 text-right">{v}</span>
                </div>
              ))}
            </div>

            {viewItem.notes && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-800">
                <p className="font-semibold text-xs uppercase tracking-wider mb-1 text-amber-600">Notes</p>
                {viewItem.notes}
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <button onClick={() => { setViewItem(null); openEdit(viewItem); }}
                className="flex-1 py-2.5 bg-orange-500 hover:bg-orange-600 text-white rounded-xl text-sm font-semibold">
                Edit Vendor
              </button>
              <button onClick={() => setDeleteId(viewItem.id)}
                className="px-4 py-2.5 bg-red-50 hover:bg-red-100 text-red-500 rounded-xl text-sm font-semibold">
                Delete
              </button>
            </div>
          </div>
        </Drawer>
      )}

      {/* ── Delete Confirm ── */}
      {deleteId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl shadow-xl p-6 w-80">
            <h3 className="font-bold text-gray-900 mb-2">Delete Vendor?</h3>
            <p className="text-sm text-gray-500 mb-5">This will permanently remove the vendor record.</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteId(null)} className="flex-1 py-2 border border-gray-200 rounded-xl text-sm font-semibold text-gray-600 hover:bg-gray-50">Cancel</button>
              <button onClick={() => handleDelete(deleteId)} className="flex-1 py-2 bg-red-500 hover:bg-red-600 text-white rounded-xl text-sm font-semibold">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VendorsPage;
